import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Search } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  /** Classes for the trigger button — pass whatever border/size/shape classes the
   *  native <select> used to have. A chevron is appended automatically, so no
   *  need to reserve padding or render one yourself. */
  className?: string;
  panelClassName?: string;
  disabled?: boolean;
  align?: 'left' | 'right';
  /** Whether to portal the dropdown to document.body to avoid clipping inside
   *  scrollable/overflow containers (e.g. table headers). Defaults to true. */
  usePortal?: boolean;
  /** Custom trigger content, e.g. to keep an existing icon/prefix label. Falls
   *  back to the selected option's label (or placeholder) when omitted. */
  renderValue?: (selected: SelectOption | undefined) => React.ReactNode;
  /** Adds a search box above the list. Matches an option's label when it is
   *  text, otherwise its value. Enter picks the first match. */
  searchable?: boolean;
  searchPlaceholder?: string;
}

// The list's height: at most max-h-72 (288px), at least enough for a few rows,
// and kept PANEL_GAP clear of the screen edge.
const PANEL_MAX_HEIGHT = 288;
const PANEL_MIN_HEIGHT = 120;
const PANEL_GAP = 18;

/** Button + dropdown panel that opens directly below its trigger. Portals to
 *  document.body by default so it is never clipped by table overflow containers. */
const Select: React.FC<SelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  className = '',
  panelClassName = '',
  disabled = false,
  align = 'left',
  usePortal = true,
  renderValue,
  searchable = false,
  searchPlaceholder = 'Search…',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  useEffect(() => { if (!isOpen) setQuery(''); }, [isOpen]);
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const [panelPos, setPanelPos] = useState<{
    top: number;
    left?: number;
    right?: number;
    minWidth: number;
    maxHeight: number;
    placement: 'bottom' | 'top';
    arrowRight: boolean;
  } | null>(null);

  const [computedAlign, setComputedAlign] = useState(align);

  const updatePosition = useCallback(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const panelEl = panelRef.current;
    const panelHeight = panelEl ? panelEl.offsetHeight : 240;
    const panelWidth = panelEl ? panelEl.offsetWidth : Math.max(rect.width, 160);

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    // Always open below, shrinking to the space there (the list scrolls
    // inside) rather than running off the screen. Only a trigger so close to
    // the bottom that not even a few rows fit opens above instead.
    const placeTop = spaceBelow < PANEL_MIN_HEIGHT + PANEL_GAP && spaceAbove > spaceBelow;
    const maxHeight = Math.max(PANEL_MIN_HEIGHT, Math.min(PANEL_MAX_HEIGHT, (placeTop ? spaceAbove : spaceBelow) - PANEL_GAP));

    const top = placeTop
      ? Math.max(8, rect.top - Math.min(panelHeight, maxHeight) - 10)
      : rect.bottom + 8;

    const isRight = align === 'right' || (rect.left + panelWidth > window.innerWidth - 8);
    let left: number | undefined = undefined;
    let right: number | undefined = undefined;

    if (isRight) {
      right = Math.max(8, window.innerWidth - rect.right);
    } else {
      left = Math.max(8, Math.min(rect.left, window.innerWidth - panelWidth - 8));
    }

    setPanelPos({
      top,
      left,
      right,
      minWidth: Math.max(rect.width, 140),
      maxHeight,
      placement: placeTop ? 'top' : 'bottom',
      arrowRight: isRight,
    });
  }, [align]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPanelPos(null);
      return;
    }
    updatePosition();
    // The first pass guesses the list's height (it isn't rendered yet); place
    // it again once it is, with the real height.
    const frame = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(frame);
  }, [isOpen, updatePosition]);

  useLayoutEffect(() => {
    if (!isOpen || !panelRef.current || usePortal) return;
    const rect = panelRef.current.getBoundingClientRect();
    if (computedAlign === 'left' && rect.right > window.innerWidth) {
      setComputedAlign('right');
    } else if (computedAlign === 'right' && rect.left < 0) {
      setComputedAlign('left');
    }
  }, [isOpen, computedAlign, usePortal]);

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = (e: Event) => {
      // Don't close if user is scrolling inside the dropdown list itself
      if (panelRef.current && panelRef.current.contains(e.target as Node)) {
        return;
      }
      setIsOpen(false);
    };
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        ref.current && !ref.current.contains(target) &&
        (!panelRef.current || !panelRef.current.contains(target))
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const selected = options.find((o) => o.value === value);
  const q = query.trim().toLowerCase();
  const shown = searchable && q
    ? options.filter((o) => (typeof o.label === 'string' ? o.label : o.value).toLowerCase().includes(q))
    : options;

  const panelContent = (
    <>
      {/* Pointer arrow */}
      <div
        className={`absolute ${panelPos?.placement === 'top' ? '-bottom-1.5 border-r border-b' : '-top-1.5 border-l border-t'} w-3 h-3 bg-white border-gray-200 rotate-45 ${
          usePortal
            ? (panelPos?.arrowRight ? 'right-4' : 'left-4')
            : (computedAlign === 'right' ? 'right-4' : 'left-4')
        }`}
      />
      <div
        style={usePortal && panelPos ? { minWidth: `${panelPos.minWidth}px`, maxHeight: `${panelPos.maxHeight}px` } : undefined}
        className={`relative min-w-full w-max max-w-xs max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-xl p-1.5 ${searchable ? 'pt-0' : ''} ${panelClassName}`}
      >
        {searchable && (
          <div className="sticky top-0 z-10 bg-white pt-1.5 pb-1.5">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  const first = shown.find((o) => !o.disabled);
                  if (first) { onChange(first.value); setIsOpen(false); }
                }}
                placeholder={searchPlaceholder}
                className="w-full h-8 pl-8 pr-2.5 rounded-lg border border-gray-200 text-xs text-gray-800 outline-none focus:border-[#1B4332] placeholder:text-gray-400"
              />
            </div>
          </div>
        )}
        {shown.length === 0 ? (
          <div className="px-3 py-2 text-xs text-gray-400">{q ? 'No matches' : 'No options'}</div>
        ) : (
          shown.map((opt) => (
            <button
              key={opt.value}
              type="button"
              disabled={opt.disabled}
              onClick={() => {
                if (opt.disabled) return;
                onChange(opt.value);
                setIsOpen(false);
              }}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
                opt.value === value ? 'bg-[#1B4332] text-white font-semibold' : 'text-gray-700 hover:bg-gray-50 font-medium'
              }`}
            >
              <span className="truncate flex-1 min-w-0">{opt.label}</span>
              {opt.value === value && <Check size={14} className="text-white shrink-0" />}
            </button>
          ))
        )}
      </div>
    </>
  );

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => {
          if (disabled) return;
          if (!isOpen) updatePosition();
          setIsOpen((v) => !v);
        }}
        disabled={disabled}
        className={`flex items-center justify-between gap-2 outline-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
      >
        <span className="truncate">{renderValue ? (renderValue(selected) ?? placeholder) : (selected?.label ?? placeholder)}</span>
        <ChevronDown size={14} className={`text-gray-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        usePortal ? (
          panelPos && typeof document !== 'undefined' ? (
            createPortal(
              <div
                ref={panelRef}
                style={{
                  position: 'fixed',
                  top: panelPos.top,
                  ...(panelPos.right !== undefined ? { right: panelPos.right } : { left: panelPos.left }),
                  zIndex: 99999,
                }}
                className="[filter:drop-shadow(0_10px_15px_rgb(0,0,0,0.1))_drop-shadow(0_4px_6px_rgb(0,0,0,0.1))] animate-in fade-in duration-150"
              >
                {panelContent}
              </div>,
              document.body
            )
          ) : null
        ) : (
          <div
            ref={panelRef}
            className={`absolute top-full mt-2.5 z-50 [filter:drop-shadow(0_10px_15px_rgb(0,0,0,0.1))_drop-shadow(0_4px_6px_rgb(0,0,0,0.1))] animate-in fade-in slide-in-from-top-1 duration-150 ${computedAlign === 'right' ? 'right-0' : 'left-0'}`}
          >
            {panelContent}
          </div>
        )
      )}
    </div>
  );
};

export default Select;
